'use client';

import type { SupabaseClient } from '@supabase/supabase-js';
import type { PlayerAssets } from 'neural-amp-modeler-wasm';
import { FORMAT } from '@/utils/constants';
import { getFeaturedModelAndTagsByToneId, getToneByIdRpc } from '@/utils/supabase/queries';
import {
  type PlayableToneRow,
  type PlayerDefaults,
  toPlayerAssets,
} from '@/utils/player/to-player-tone';

/**
 * Lazy asset loader for cards: nothing is fetched until the player first
 * needs the tone's models. The player dedupes and caches the result per tone.
 *
 * `featuredOnly` fetches just the first playable model (carousel mini
 * players, which never show a select); otherwise the full A2-filtered model
 * list comes from the `get_tone_with_models` RPC in the owner's order.
 */
export const toneAssetsClient =
  (
    supabase: SupabaseClient,
    tone: PlayableToneRow,
    config: PlayerDefaults,
    { featuredOnly = false }: { featuredOnly?: boolean } = {}
  ) =>
  async (): Promise<PlayerAssets> => {
    const id = Number(tone.id);
    const platform = (tone.platform as FORMAT) || FORMAT.NAM;

    if (featuredOnly) {
      const featured = await getFeaturedModelAndTagsByToneId(supabase, id, platform);
      if (!featured) throw new Error('This tone has nothing the player can preview.');
      const assets = toPlayerAssets([featured.model], { ...tone, tags: featured.tags }, config);
      if (!assets) throw new Error('This tone has nothing the player can preview.');
      return assets;
    }

    // Server-side A2 filter; `toPlayerAssets` re-checks client-side.
    const full = await getToneByIdRpc(supabase, id, ['2']);
    if (!full) throw new Error('This tone is unavailable.');
    const assets = toPlayerAssets(full.models ?? [], full, config);
    if (!assets) throw new Error('This tone has no A2 models to preview yet.');
    return assets;
  };
